import {Card, Modal, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {FormattedMessage} from 'react-intl'
import {GET_AGENTS_RENEWAL_RATE} from '../../../gql/queries/reportsQuery'
import './AdvisorsCompletedPoliciesTable.css'

interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface IAgentRenewalData {
  user: IUser
  completedCount: number
  renewedCount: number
  totalActiveCount: number
  totalNonCanceledCount: number
  totalCount: number
  canceledCount: number
  completionPercentage: number
  renewalPercentage: number
  canceledPercentage: number
  rank: number
}

interface IResAgentsRenewal {
  agentsRenewalRate: IAgentRenewalData[]
}

interface IAgentsRenewalRateTableProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  completedCategoryCodes?: string[]
  nonCanceledCategoryCodes?: string[]
  canceledCategoryCodes?: string[]
  onOpenPolicies?: (extraFilters: Record<string, unknown>) => void
  refreshTrigger?: number
}

type MetricColumn =
  | 'completed'
  | 'renewed'
  | 'totalActive'
  | 'totalNonCanceled'
  | 'canceled'
  | 'total'

type SortField = 'agent' | 'completed' | 'completionPercentage' | 'renewed' | 'renewalPercentage' | 'totalActive' | 'totalNonCanceled' | 'total' | 'canceled' | 'canceledPercentage'
type SortOrder = 'asc' | 'desc'

export const AgentsRenewalRateTable = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  completedCategoryCodes,
  nonCanceledCategoryCodes,
  canceledCategoryCodes,
  onOpenPolicies,
  refreshTrigger,
}: IAgentsRenewalRateTableProps) => {
  const [showModal, setShowModal] = useState(false)

  const buildColumnFilters = (entry: IAgentRenewalData, column: MetricColumn): Record<string, unknown> => {
    const base = {user: [entry.user.id]}
    switch (column) {
      case 'completed':
        return {...base, status: ['COMPLETED']}
      case 'renewed':
        return {...base, status: ['RENEWED', 'RENOVACION_AUTOMATICA_MERCADO']}
      case 'totalActive':
        return {...base, status: completedCategoryCodes}
      case 'totalNonCanceled':
        return {...base, status: nonCanceledCategoryCodes}
      case 'canceled':
        return {...base, status: canceledCategoryCodes}
      case 'total':
        return base
    }
  }

  const openPoliciesFor = (entry: IAgentRenewalData, column: MetricColumn) =>
    onOpenPolicies?.(buildColumnFilters(entry, column))
  const [sortField, setSortField] = useState<SortField>('completed')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getTopAgents, {data, loading}] = useLazyQuery<IResAgentsRenewal>(
    GET_AGENTS_RENEWAL_RATE,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getAllAgents, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResAgentsRenewal>(GET_AGENTS_RENEWAL_RATE, {
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    getTopAgents({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, agent, paymentStatus, advisorPaid, refreshTrigger, getTopAgents])

  const handleShowMore = () => {
    getAllAgents({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'agent' ? 'asc' : 'desc')
    }
  }

  const agentsData = data?.agentsRenewalRate || []
  const fullAgentsData = fullData?.agentsRenewalRate || []

  const sortedFullAgents = [...fullAgentsData].sort((a, b) => {
    if (sortField === 'agent') {
      const nameA = `${a.user.firstName} ${a.user.lastName}`
      const nameB = `${b.user.firstName} ${b.user.lastName}`
      const comparison = nameA.localeCompare(nameB)
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'completed') {
      const comparison = a.completedCount - b.completedCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'completionPercentage') {
      const comparison = a.completionPercentage - b.completionPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'renewed') {
      const comparison = a.renewedCount - b.renewedCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'renewalPercentage') {
      const comparison = a.renewalPercentage - b.renewalPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'totalActive') {
      const comparison = a.totalActiveCount - b.totalActiveCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'totalNonCanceled') {
      const comparison = a.totalNonCanceledCount - b.totalNonCanceledCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'canceled') {
      const comparison = a.canceledCount - b.canceledCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'canceledPercentage') {
      const comparison = a.canceledPercentage - b.canceledPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.totalCount - b.totalCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatPercent = (num: number, digits = 2) => `${num.toFixed(digits)}%`

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              <FormattedMessage
                id='dashboard.policies.agents.title'
                defaultMessage='Agents performance metrics (Top 10)'
              />
            </span>
            <span className='text-muted fw-semibold fs-7'>
              <FormattedMessage
                id='dashboard.policies.agents.subtitle'
                defaultMessage='Agents with highest completed policies count'
              />
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loading || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              <FormattedMessage id='dashboard.policies.common.seeMore' defaultMessage='See more' />
            </button>
          </div>
        </Card.Header>
        <Card.Body className='pt-0'>
          {(loading || parentLoading) && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loading && !parentLoading && agentsData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <p className='text-muted'>
                <FormattedMessage
                  id='dashboard.policies.common.noDataPeriod'
                  defaultMessage='No data available for the selected period'
                />
              </p>
            </div>
          )}
          {!loading && !parentLoading && agentsData.length > 0 && (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0' style={{width: '5%'}}></th>
                    <th style={{width: '15%'}}>
                      <FormattedMessage id='common.field.agent' defaultMessage='Agent' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.completed' defaultMessage='Completed' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.completedPercent' defaultMessage='Completed %' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.renewed' defaultMessage='Renewed' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.renewalPercent' defaultMessage='Renewal %' />
                    </th>
                    <th className='text-center' style={{width: '9%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.totalActive' defaultMessage='Total Active' />
                    </th>
                    <th className='text-center' style={{width: '10%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.totalNonCanceled' defaultMessage='Total Non-Canceled' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='common.field.total' defaultMessage='Total' />
                    </th>
                    <th className='text-center' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.canceled' defaultMessage='Canceled' />
                    </th>
                    <th className='text-center pe-0' style={{width: '8%'}}>
                      <FormattedMessage id='dashboard.policies.agents.col.canceledPercent' defaultMessage='Canceled %' />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {agentsData.map((entry, index) => (
                    <tr key={entry.user.id}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-info d-flex align-items-center justify-content-center me-3'
                            style={{width: 30, height: 30}}
                          >
                            <span className='text-info fw-bold fs-7'>
                              {getInitials(entry.user.firstName, entry.user.lastName)}
                            </span>
                          </div>
                          <span className='text-gray-800 fw-bold fs-7'>
                            {entry.user.firstName} {entry.user.lastName}
                          </span>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'completed')}
                        >
                          {formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-success fs-8'>
                          {formatPercent(entry.completionPercentage)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'renewed')}
                        >
                          {formatNumber(entry.renewedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-info fs-8'>
                          {formatPercent(entry.renewalPercentage)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalActive')}
                        >
                          {formatNumber(entry.totalActiveCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalNonCanceled')}
                        >
                          {formatNumber(entry.totalNonCanceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'total')}
                        >
                          {formatNumber(entry.totalCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'canceled')}
                        >
                          {formatNumber(entry.canceledCount)}
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span className='badge badge-light-danger fs-8'>
                          {formatPercent(entry.canceledPercentage)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for all agents */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='xl' dialogClassName='modal-90w'>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              id='dashboard.policies.agents.modalTitle'
              defaultMessage='Agents performance metrics'
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullAgentsData.length === 0 && (
            <div className='text-center text-muted py-5'>
              <FormattedMessage
                id='dashboard.policies.common.noData'
                defaultMessage='No data available'
              />
            </div>
          )}
          {!loadingFull && fullAgentsData.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0 text-center' style={{width: '3%'}}></th>
                    <th
                      style={{width: '12%', cursor: 'pointer'}}
                      onClick={() => handleSort('agent')}
                    >
                      <FormattedMessage id='common.field.agent' defaultMessage='Agent' />
                      {getSortIcon('agent')}
                    </th>
                    <th className='text-center' style={{width: '12%'}}>
                      <FormattedMessage id='common.field.email' defaultMessage='Email' />
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('completed')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.completed' defaultMessage='Completed' />
                      {getSortIcon('completed')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('completionPercentage')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.completedPercent' defaultMessage='Completed %' />
                      {getSortIcon('completionPercentage')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('renewed')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.renewed' defaultMessage='Renewed' />
                      {getSortIcon('renewed')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('renewalPercentage')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.renewalPercent' defaultMessage='Renewal %' />
                      {getSortIcon('renewalPercentage')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '8%', cursor: 'pointer'}}
                      onClick={() => handleSort('totalActive')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.totalActive' defaultMessage='Total Active' />
                      {getSortIcon('totalActive')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '9%', cursor: 'pointer'}}
                      onClick={() => handleSort('totalNonCanceled')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.totalNonCanceled' defaultMessage='Total Non-Canceled' />
                      {getSortIcon('totalNonCanceled')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('total')}
                    >
                      <FormattedMessage id='common.field.total' defaultMessage='Total' />
                      {getSortIcon('total')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('canceled')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.canceled' defaultMessage='Canceled' />
                      {getSortIcon('canceled')}
                    </th>
                    <th
                      className='text-center pe-0'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('canceledPercentage')}
                    >
                      <FormattedMessage id='dashboard.policies.agents.col.canceledPercent' defaultMessage='Canceled %' />
                      {getSortIcon('canceledPercentage')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullAgents.map((entry, index) => (
                    <tr key={entry.user.id}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-info d-flex align-items-center justify-content-center me-3'
                            style={{width: 30, height: 30}}
                          >
                            <span className='text-info fw-bold fs-7'>
                              {getInitials(entry.user.firstName, entry.user.lastName)}
                            </span>
                          </div>
                          <span className='text-gray-800 fw-bold fs-7'>
                            {entry.user.firstName} {entry.user.lastName}
                          </span>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span className='text-muted fs-8'>{entry.user.email}</span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'completed')}
                        >
                          {formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-success fs-8'>
                          {formatPercent(entry.completionPercentage)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'renewed')}
                        >
                          {formatNumber(entry.renewedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-info fs-8'>
                          {formatPercent(entry.renewalPercentage)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalActive')}
                        >
                          {formatNumber(entry.totalActiveCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalNonCanceled')}
                        >
                          {formatNumber(entry.totalNonCanceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'total')}
                        >
                          {formatNumber(entry.totalCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-7'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'canceled')}
                        >
                          {formatNumber(entry.canceledCount)}
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span className='badge badge-light-danger fs-8'>
                          {formatPercent(entry.canceledPercentage)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
